import { DefinedPropertyObject, IEntityMinimalModel } from "../../../api/GenericTypes";
import { LabNotebookEntry, LabNotebookEntryFilters } from "../../types/LabNotebookEntry";
import { LabNotebookMinimalModel } from "../../types/LabNotebook";
import { LabNotebookExperimentMinimalModel } from "../../types/LabNotebookExperiment";
import {
  TabStoreFilterSidebarProps,
  TabStoreFilterFormProvider,
} from "../../../common/sidebar/filtersidebar/TabStoreFilterFormProvider";
import { EntityFilterSideBar } from "../../../common/sidebar/filtersidebar/EntityFilterBar";
import { LabNotebooksVirtualizedSelectForm } from "../../../common/forms/EntityForms/formsVirtualized/LabNotebooksVirtualizedSelectFormField";
import { LabNotebookExperimentsVirtualizedSelectForm } from "../../../common/forms/EntityForms/formsVirtualized/LabNotebookExperimentsVirtualizedSelectForm";
import { toValidDate } from "../../../common/datetime/DateTimeFormatter";
import { ProjectsVirtualizedSelectForm } from "../../../common/forms/EntityForms/formsVirtualized/ProjectsVirtualizedSelectForm";
import {
  CombinedModificationRecordEntityDefaultFormValues,
  combinedModificationRecordEntityFiltersToForm,
  combinedModificationRecordEntityFormToFilters,
  ICombinedModificationRecordEntityForm,
} from "../../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterFormModels";
import {
  ILockableEntityForm,
  LockableEntityDefaultFilterFormValues,
  lockableEntityFiltersToForm,
  lockableEntityFormToFilters,
} from "../../../common/sidebar/filtersidebar/GenericFilters/LockableEntityFilterFormModels";
import {
  ISignableEntityForm,
  SignableEntityDefaultFilterFormValues,
  signableEntityFiltersToForm,
  signableEntityFormToFilters,
} from "../../../common/sidebar/filtersidebar/GenericFilters/SignableEntityFilterFormModels";
import {
  ISoftDeletableEntityForm,
  SoftDeletableEntityDefaultFilterFormValues,
  softDeletableEntityFiltersToForm,
  softDeletableEntityFormToFilters,
} from "../../../common/sidebar/filtersidebar/GenericFilters/SoftDeletableEntityFilterFormModels";
import { CombinedModificationRecordEntityFilterForm } from "../../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterForm";
import { SignableEntityFilterForm } from "../../../common/sidebar/filtersidebar/GenericFilters/SignableEntityFilterForm";
import { defaultLabNotebookEntriesFilters } from "./LabNotebookEntriesTable";

interface LabNotebookEntriesFilterBarProps
  extends TabStoreFilterSidebarProps<LabNotebookEntry, LabNotebookEntriesFilterForm, LabNotebookEntryFilters> {
  labNotebookId?: number;
  labNotebookExperimentId?: number;
}

export interface LabNotebookEntriesFilterForm
  extends ICombinedModificationRecordEntityForm,
    ILockableEntityForm,
    ISignableEntityForm,
    ISoftDeletableEntityForm {
  labNotebookIds?: LabNotebookMinimalModel[] | undefined | null;
  labNotebookExperimentIds?: LabNotebookExperimentMinimalModel[] | undefined | null;
  entryDateFrom?: Date | undefined | null;
  entryDateTo?: Date | undefined | null;
  labNotebookProjectIds?: IEntityMinimalModel[] | undefined | null;
}
const defaultValues: DefinedPropertyObject<LabNotebookEntriesFilterForm> = {
  labNotebookIds: null,
  labNotebookExperimentIds: null,
  entryDateFrom: null,
  entryDateTo: null,
  labNotebookProjectIds: null,
  ...CombinedModificationRecordEntityDefaultFormValues,
  ...LockableEntityDefaultFilterFormValues,
  ...SignableEntityDefaultFilterFormValues,
  ...SoftDeletableEntityDefaultFilterFormValues,
};
export const labNotebookEntriesFormToFilters = (form: LabNotebookEntriesFilterForm): LabNotebookEntryFilters => {
  let filters: LabNotebookEntryFilters = {
    ...combinedModificationRecordEntityFormToFilters(form),
    ...lockableEntityFormToFilters(form),
    ...signableEntityFormToFilters(form),
    ...softDeletableEntityFormToFilters(form),
  };
  filters.entryDateFrom = toValidDate(form.entryDateFrom);
  filters.entryDateTo = toValidDate(form.entryDateTo);
  filters.labNotebookReferenceIds =
    Array.isArray(form.labNotebookIds) && form.labNotebookIds.length > 0 ? form.labNotebookIds?.map((d) => d.id) : null;
  filters.labNotebookExperimentReferenceIds =
    Array.isArray(form.labNotebookExperimentIds) && form.labNotebookExperimentIds.length > 0
      ? form.labNotebookExperimentIds?.map((d) => d.id)
      : null;
  filters.labNotebookProjectIds =
    Array.isArray(form.labNotebookProjectIds) && form.labNotebookProjectIds.length > 0
      ? form.labNotebookProjectIds?.map((d) => d.id)
      : null;
  return filters;
};

/**
 * Converts LabNotebookEntryFilters to LabNotebookEntriesFilterForm
 * @param filters LabNotebookEntryFilters
 * @returns LabNotebookEntriesFilterForm
 * @author @CorradoSurmanowicz
 */
export const filtersToLabNotebookEntriesForm = (filters: LabNotebookEntryFilters): LabNotebookEntriesFilterForm => {
  return {
    entryDateFrom: filters.entryDateFrom ? new Date(filters.entryDateFrom) : null,
    entryDateTo: filters.entryDateTo ? new Date(filters.entryDateTo) : null,
    labNotebookIds: filters.labNotebookReferenceIds
      ? filters.labNotebookReferenceIds.map((id) => ({ id, name: "", status: "ACTIVE" }))
      : null,
    labNotebookExperimentIds: filters.labNotebookExperimentReferenceIds
      ? filters.labNotebookExperimentReferenceIds.map((id) => ({ id, name: "", status: "RUNNING" }))
      : null,
    labNotebookProjectIds: filters.labNotebookProjectIds
      ? filters.labNotebookProjectIds.map((id) => ({ id, name: "" }))
      : null,
    ...combinedModificationRecordEntityFiltersToForm(filters),
    ...lockableEntityFiltersToForm(filters),
    ...signableEntityFiltersToForm(filters),
    ...softDeletableEntityFiltersToForm(filters),
  };
};

export const LabNotebookEntriesFilterBar = ({
  initialValues,
  onFormChange,
  dispatchTabStore,
  tabsLoading,
  currentTab,
  labNotebookId,
  labNotebookExperimentId,
}: LabNotebookEntriesFilterBarProps) => {
  return (
    <TabStoreFilterFormProvider
      tabsLoading={tabsLoading}
      initialValues={initialValues}
      defaultValues={defaultValues}
      defaultFilters={defaultLabNotebookEntriesFilters}
      formToFilters={labNotebookEntriesFormToFilters}
      onFormChange={onFormChange}
      dispatchTabStore={dispatchTabStore}
    >
      {({ control, onReset }) => (
        <EntityFilterSideBar onReset={onReset}>
          {!labNotebookId && (
            <LabNotebooksVirtualizedSelectForm id="labNotebookIds" label="Notebook" control={control} isMulti />
          )}
          {!labNotebookExperimentId && (
            <LabNotebookExperimentsVirtualizedSelectForm
              id="labNotebookExperimentIds"
              label="Experiment"
              control={control}
              filters={{ labNotebookIds: !!labNotebookId ? [labNotebookId] : null }}
              isMulti
            />
          )}

          {!labNotebookId && !labNotebookExperimentId && (
            <ProjectsVirtualizedSelectForm id="labNotebookProjectIds" control={control} showControls={false} isMulti />
          )}

          <CombinedModificationRecordEntityFilterForm
            control={control}
            currentTab={currentTab}
            initialValues={initialValues}
          />

          <SignableEntityFilterForm control={control} initialValues={initialValues} />

          {/* <SoftDeletableEntityFilterForm control={control} currentTab={currentTab} initialValues={initialValues} /> */}
        </EntityFilterSideBar>
      )}
    </TabStoreFilterFormProvider>
  );
};
